import { GeojsonCoordinate } from '/@/types/map'

const LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50
const CENTRAL_DIRECTORY_HEADER_SIGNATURE = 0x02014b50
const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50
const ZIP64_FIELD_VALUE = 0xffffffff
const ZIP_METHOD_STORED = 0
const ZIP_METHOD_DEFLATED = 8
const MAX_ZIP_COMMENT_LENGTH = 0xffff
const COORDINATE_PATTERN = /[-+]?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?\s*,\s*[-+]?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?(?:\s*,\s*[-+]?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?)?/g

interface ZipEntry {
  name: string
  method: number
  compressedSize: number
  localHeaderOffset: number
}

export interface WaylineXmlDocument {
  name: string
  xml: string
}

export interface ParsedWaylinePoint {
  coordinate: GeojsonCoordinate
  height: number
  /** WPML actuator functions (plus interval trigger types) configured at this waypoint, in file order. */
  actions?: string[]
  sourceHeight: number | null
  missingHeight: boolean
  sourceHeightField?: 'executeHeight' | 'ellipsoidHeight' | 'height' | 'coordinateHeight' | null
  ellipsoidHeight?: number | null
}

export interface ParsedWaylineRoute {
  id: string
  heightMode: string
  points: ParsedWaylinePoint[]
}

export interface ParsedWaylineTakeoff {
  coordinate: GeojsonCoordinate
  ellipsoidHeight: number | null
  altitudeHeight: number | null
  fallback: boolean
}

export interface ParsedWaylinePreview {
  routes: ParsedWaylineRoute[]
  takeoff: ParsedWaylineTakeoff | null
  missingHeight: boolean
}

export interface EllipsoidWaylinePreview {
  preview: ParsedWaylinePreview
  canRender3D: boolean
  unresolvedPointCount: number
  takeoffHeight: number | null
}

/**
 * The existing preview is takeoff-relative. A georeferenced model instead needs
 * WGS84 ellipsoid heights, without guessing from a model surface or EGM96 value.
 * Return a clone so switching the model off restores the original preview.
 */
export function resolveWaylineEllipsoidHeights (preview: ParsedWaylinePreview): EllipsoidWaylinePreview {
  const rawTakeoffHeight = preview.takeoff?.ellipsoidHeight
  const takeoffHeight = typeof rawTakeoffHeight === 'number' && Number.isFinite(rawTakeoffHeight) ? rawTakeoffHeight : null
  let unresolvedPointCount = 0
  let pointCount = 0
  const routes = preview.routes.map(route => ({
    ...route,
    points: route.points.map(point => {
      pointCount++
      const sourceHeight = point.sourceHeight
      let ellipsoidHeight: number | null = null
      if (!point.missingHeight && sourceHeight !== null && Number.isFinite(sourceHeight)) {
        const relative = route.heightMode.toLowerCase() === 'relativetostartpoint'
        // WPML keeps wpml:ellipsoidHeight equal to the relative wpml:height in
        // relativeToStartPoint templates, so it is only an absolute height
        // under the other modes.
        if ((point.sourceHeightField === 'ellipsoidHeight' && !relative) || route.heightMode.toLowerCase() === 'wgs84') {
          ellipsoidHeight = sourceHeight
        } else if (relative && takeoffHeight !== null) {
          ellipsoidHeight = sourceHeight + takeoffHeight
        }
      }
      if (ellipsoidHeight === null) {
        unresolvedPointCount++
      }
      return { ...point, coordinate: [...point.coordinate] as GeojsonCoordinate, height: ellipsoidHeight ?? point.height, ellipsoidHeight }
    }),
  }))
  return {
    preview: {
      ...preview,
      routes,
      takeoff: preview.takeoff ? { ...preview.takeoff, coordinate: [...preview.takeoff.coordinate] as GeojsonCoordinate } : null,
    },
    canRender3D: pointCount > 0 && unresolvedPointCount === 0,
    unresolvedPointCount,
    takeoffHeight,
  }
}

interface RawWaylinePoint {
  coordinate: GeojsonCoordinate
  actions: string[]
  executeHeight: number | null
  ellipsoidHeight: number | null
  height: number | null
  coordinateHeight: number | null
}

interface RawWaylineRoute {
  id: string
  heightMode: string
  points: RawWaylinePoint[]
  order: number
}

interface RawTakeoff {
  coordinate: GeojsonCoordinate
  ellipsoidHeight: number | null
  altitudeHeight: number | null
}

export async function parseKmzWaylinePreview (file: Blob): Promise<ParsedWaylinePreview> {
  return parseWaylinePreviewDocuments(await readKmzWaylineDocuments(file))
}

/** Extract every template.kml / waylines.wpml text entry of a KMZ, execution file first. */
export async function readKmzWaylineDocuments (file: Blob): Promise<WaylineXmlDocument[]> {
  const buffer = await file.arrayBuffer()
  const entries = getWaylineTextEntries(readZipEntries(buffer))
  const documents: WaylineXmlDocument[] = []

  for (const entry of entries) {
    const bytes = await decodeZipEntry(buffer, entry)
    documents.push({
      name: entry.name,
      xml: new TextDecoder('utf-8').decode(bytes),
    })
  }

  return documents
}

export function parseWaylinePreviewDocuments (documents: WaylineXmlDocument[]): ParsedWaylinePreview {
  const parsedDocuments = documents
    .map(document => ({
      name: document.name,
      document: new DOMParser().parseFromString(document.xml, 'application/xml'),
    }))
    .filter(item => !hasParserError(item.document))

  const takeoff = parsedDocuments
    .map(item => extractTakeoff(item.document))
    .find((value): value is RawTakeoff => Boolean(value)) || null

  const executableRoutes = parsedDocuments
    .filter(item => item.name.toLowerCase().endsWith('.wpml'))
    .flatMap(item => extractRoutes(item.document, 'executeHeightMode'))
    .filter(route => route.points.length > 0)

  const templateRoutes = parsedDocuments
    .filter(item => item.name.toLowerCase().endsWith('.kml'))
    .flatMap(item => extractRoutes(item.document, 'heightMode'))
    .filter(route => route.points.length > 0)

  const rawRoutes = (executableRoutes.length > 0 ? executableRoutes : templateRoutes)
    .sort(compareRoutes)
  const routes = rawRoutes.map(route => normalizeRoute(route, takeoff))
    .filter(route => route.points.length > 0)
  const firstPoint = routes[0]?.points[0]
  const normalizedTakeoff = takeoff
    ? {
        coordinate: takeoff.coordinate,
        ellipsoidHeight: takeoff.ellipsoidHeight,
        altitudeHeight: takeoff.altitudeHeight,
        fallback: false,
      }
    : firstPoint
      ? {
          coordinate: [firstPoint.coordinate[0], firstPoint.coordinate[1]] as GeojsonCoordinate,
          ellipsoidHeight: null,
          altitudeHeight: null,
          fallback: true,
        }
      : null

  return {
    routes,
    takeoff: normalizedTakeoff,
    missingHeight: routes.some(route => route.points.some(point => point.missingHeight)),
  }
}

function readZipEntries (buffer: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buffer)
  const endOfCentralDirectoryOffset = findEndOfCentralDirectory(view)
  const totalEntries = view.getUint16(endOfCentralDirectoryOffset + 10, true)
  const centralDirectoryOffset = view.getUint32(endOfCentralDirectoryOffset + 16, true)

  if (centralDirectoryOffset === ZIP64_FIELD_VALUE) {
    throw new Error('ZIP64 KMZ files are not supported.')
  }

  const entries: ZipEntry[] = []
  let offset = centralDirectoryOffset
  const decoder = new TextDecoder('utf-8')

  for (let i = 0; i < totalEntries; i++) {
    if (view.getUint32(offset, true) !== CENTRAL_DIRECTORY_HEADER_SIGNATURE) {
      throw new Error('Invalid KMZ central directory.')
    }

    const method = view.getUint16(offset + 10, true)
    const compressedSize = view.getUint32(offset + 20, true)
    const fileNameLength = view.getUint16(offset + 28, true)
    const extraFieldLength = view.getUint16(offset + 30, true)
    const fileCommentLength = view.getUint16(offset + 32, true)
    const localHeaderOffset = view.getUint32(offset + 42, true)
    const fileNameStart = offset + 46
    const name = decoder.decode(new Uint8Array(buffer, fileNameStart, fileNameLength))

    if (compressedSize === ZIP64_FIELD_VALUE || localHeaderOffset === ZIP64_FIELD_VALUE) {
      throw new Error('ZIP64 KMZ files are not supported.')
    }

    entries.push({
      name,
      method,
      compressedSize,
      localHeaderOffset,
    })

    offset = fileNameStart + fileNameLength + extraFieldLength + fileCommentLength
  }

  return entries
}

function findEndOfCentralDirectory (view: DataView): number {
  const minOffset = Math.max(0, view.byteLength - 22 - MAX_ZIP_COMMENT_LENGTH)

  for (let offset = view.byteLength - 22; offset >= minOffset; offset--) {
    if (view.getUint32(offset, true) === END_OF_CENTRAL_DIRECTORY_SIGNATURE) {
      return offset
    }
  }

  throw new Error('Invalid KMZ file.')
}

function getWaylineTextEntries (entries: ZipEntry[]): ZipEntry[] {
  return entries
    .filter(entry => isWaylineTextEntry(entry.name))
    .sort((a, b) => getWaylineEntryPriority(a.name) - getWaylineEntryPriority(b.name))
}

function isWaylineTextEntry (name: string): boolean {
  const lowerName = name.toLowerCase()
  return lowerName.endsWith('.wpml') || lowerName.endsWith('.kml')
}

function getWaylineEntryPriority (name: string): number {
  const lowerName = name.toLowerCase()
  if (lowerName.endsWith('/waylines.wpml') || lowerName === 'waylines.wpml') {
    return 0
  }
  if (lowerName.endsWith('.wpml')) {
    return 1
  }
  if (lowerName.endsWith('/template.kml') || lowerName === 'template.kml') {
    return 2
  }
  return 3
}

async function decodeZipEntry (buffer: ArrayBuffer, entry: ZipEntry): Promise<Uint8Array> {
  const view = new DataView(buffer)
  const localHeaderOffset = entry.localHeaderOffset

  if (view.getUint32(localHeaderOffset, true) !== LOCAL_FILE_HEADER_SIGNATURE) {
    throw new Error('Invalid KMZ local file header.')
  }

  const fileNameLength = view.getUint16(localHeaderOffset + 26, true)
  const extraFieldLength = view.getUint16(localHeaderOffset + 28, true)
  const dataStart = localHeaderOffset + 30 + fileNameLength + extraFieldLength
  const compressedData = new Uint8Array(buffer.slice(dataStart, dataStart + entry.compressedSize))

  if (entry.method === ZIP_METHOD_STORED) {
    return compressedData
  }
  if (entry.method === ZIP_METHOD_DEFLATED) {
    return inflateRaw(compressedData)
  }

  throw new Error(`Unsupported KMZ compression method: ${entry.method}.`)
}

async function inflateRaw (data: Uint8Array): Promise<Uint8Array> {
  const DecompressionStreamCtor = (window as any).DecompressionStream
  if (!DecompressionStreamCtor) {
    throw new Error('This browser does not support KMZ decompression.')
  }

  const stream = new Blob([data]).stream().pipeThrough(new DecompressionStreamCtor('deflate-raw'))
  const buffer = await new Response(stream as any).arrayBuffer()
  return new Uint8Array(buffer)
}

function hasParserError (document: Document): boolean {
  return Array.from(document.getElementsByTagName('*'))
    .some(element => localName(element) === 'parsererror')
}

function extractTakeoff (document: Document): RawTakeoff | null {
  const text = findFirstText(document, 'takeOffRefPoint')
  if (!text) {
    return null
  }
  const values = text.split(',').map(value => Number(value.trim()))
  const lat = values[0]
  const lng = values[1]
  const ellipsoidHeight = finiteNumber(values[2])
  if (!isValidCoordinate(lng, lat)) {
    return null
  }
  return {
    coordinate: [lng, lat],
    ellipsoidHeight,
    altitudeHeight: finiteText(findFirstText(document, 'takeOffRefPointAGLHeight')),
  }
}

function extractRoutes (document: Document, heightModeName: string): RawWaylineRoute[] {
  const folders = findElements(document, 'Folder')
  const routeElements = folders.length > 0 ? folders : [document.documentElement]

  return routeElements.map((routeElement, order) => {
    const routeId = findFirstText(routeElement, 'waylineId') ||
      findFirstText(routeElement, 'templateId') || String(order)
    const heightMode = findFirstText(routeElement, heightModeName) ||
      findFirstText(document, heightModeName) || 'unknown'
    const points = findElements(routeElement, 'Placemark')
      .map(extractPlacemarkPoint)
      .filter((point): point is RawWaylinePoint => Boolean(point))

    return {
      id: routeId,
      heightMode,
      points,
      order,
    }
  })
}

function extractPlacemarkPoint (placemark: Element): RawWaylinePoint | null {
  const pointElement = findElements(placemark, 'Point')[0]
  if (!pointElement) {
    return null
  }
  const coordinateText = findFirstText(pointElement, 'coordinates')
  const coordinate = coordinateText ? parseCoordinateText(coordinateText)[0] : extractLatitudeLongitudePair(placemark)
  if (!coordinate) {
    return null
  }

  return {
    coordinate: [coordinate[0], coordinate[1]],
    actions: extractPlacemarkActions(placemark),
    executeHeight: finiteText(findFirstText(placemark, 'executeHeight')),
    ellipsoidHeight: finiteText(findFirstText(placemark, 'ellipsoidHeight')),
    height: finiteText(findFirstText(placemark, 'height')),
    coordinateHeight: finiteNumber(coordinate[2]),
  }
}

/** Action names of a waypoint: interval triggers first (they span segments), then reach-point actions in order. */
function extractPlacemarkActions (placemark: Element): string[] {
  const actions: string[] = []
  findElements(placemark, 'actionGroup').forEach(group => {
    const trigger = findFirstText(group, 'actionTriggerType')
    if (trigger === 'multipleTiming' || trigger === 'multipleDistance') {
      actions.unshift(trigger)
      return
    }
    findElements(group, 'action').forEach(action => {
      const func = findFirstText(action, 'actionActuatorFunc')
      if (func) actions.push(func)
    })
  })
  return actions
}

function normalizeRoute (route: RawWaylineRoute, takeoff: RawTakeoff | null): ParsedWaylineRoute {
  const points = route.points.map(point => normalizePoint(point, route.heightMode, takeoff))
    .filter((point, index, allPoints) => {
      if (index === 0) {
        return true
      }
      const previous = allPoints[index - 1]
      return point.coordinate[0] !== previous.coordinate[0] ||
        point.coordinate[1] !== previous.coordinate[1] ||
        point.height !== previous.height
    })

  return {
    id: route.id,
    heightMode: route.heightMode,
    points,
  }
}

function normalizePoint (point: RawWaylinePoint, heightMode: string, takeoff: RawTakeoff | null): ParsedWaylinePoint {
  const sourceHeightField = (['executeHeight', 'ellipsoidHeight', 'height', 'coordinateHeight'] as const)
    .find(field => point[field] !== null && Number.isFinite(point[field])) || null
  const sourceHeight = sourceHeightField ? point[sourceHeightField] : null
  const missingHeight = sourceHeight === null
  let displayHeight = sourceHeight ?? 0

  if (!missingHeight && heightMode.toLowerCase() === 'wgs84' && takeoff?.ellipsoidHeight !== null && takeoff?.ellipsoidHeight !== undefined) {
    displayHeight -= takeoff.ellipsoidHeight
  }

  return {
    coordinate: point.coordinate,
    height: displayHeight,
    actions: point.actions,
    sourceHeight,
    sourceHeightField,
    ellipsoidHeight: point.ellipsoidHeight,
    missingHeight,
  }
}

function compareRoutes (left: RawWaylineRoute, right: RawWaylineRoute): number {
  const leftId = Number(left.id)
  const rightId = Number(right.id)
  if (Number.isFinite(leftId) && Number.isFinite(rightId) && leftId !== rightId) {
    return leftId - rightId
  }
  return left.order - right.order
}

function findElements (root: Document | Element, name: string): Element[] {
  const normalizedName = name.toLowerCase()
  return Array.from(root.getElementsByTagName('*'))
    .filter(element => localName(element) === normalizedName)
}

function findFirstText (root: Document | Element, name: string): string {
  return (findElements(root, name)[0]?.textContent || '').trim()
}

function localName (element: Element): string {
  return (element.localName || element.nodeName.split(':').pop() || '').toLowerCase()
}

function parseCoordinateText (text: string): GeojsonCoordinate[] {
  const matches = text.match(COORDINATE_PATTERN) || []
  return matches
    .map(parseCoordinateTuple)
    .filter((coordinate): coordinate is GeojsonCoordinate => Boolean(coordinate))
}

function parseCoordinateTuple (tuple: string): GeojsonCoordinate | null {
  const values = tuple.split(',').map(value => Number(value.trim()))
  const lng = values[0]
  const lat = values[1]
  const altitude = values[2]

  if (!isValidCoordinate(lng, lat)) {
    return null
  }

  return Number.isFinite(altitude) ? [lng, lat, altitude] : [lng, lat]
}

function extractLatitudeLongitudePair (root: Element): GeojsonCoordinate | null {
  const lng = Number(findFirstText(root, 'longitude') || findFirstText(root, 'lng'))
  const lat = Number(findFirstText(root, 'latitude') || findFirstText(root, 'lat'))
  const altitude = finiteText(findFirstText(root, 'altitude'))
  if (!isValidCoordinate(lng, lat)) {
    return null
  }
  return altitude === null ? [lng, lat] : [lng, lat, altitude]
}

function finiteText (value: string): number | null {
  if (!value) {
    return null
  }
  return finiteNumber(Number(value))
}

function finiteNumber (value: unknown): number | null {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function isValidCoordinate (lng: number, lat: number): boolean {
  return Number.isFinite(lng) &&
    Number.isFinite(lat) &&
    lng >= -180 &&
    lng <= 180 &&
    lat >= -90 &&
    lat <= 90
}
